import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import { useVideoThumbnail } from '../useVideoThumbnail';
import { useShake } from '../ShakeProvider';

interface Job {
  id: string;
  name: string;
  status?: string;
  created_time?: string;
  createdTime?: string;
  startTime?: string;
  start_time?: string;
  estimated_wait_time?: number | string;
  estimated_processing_time?: number | string;
}

interface Draft {
  id: string;
  name: string;
  savedAt: string;
}

interface ExampleVideo {
  name: string;
  url: string;
}

interface Template {
  id: string;
  name: string;
  jobs: Job[];
  example_videos?: ExampleVideo[];
}

interface JobListProps {
  template: Template;
}

function getRemainingSeconds(job: Job, nowMs: number): number | null {
  if (!job) return null;

  const status = job.status;
  if (status !== 'pending' && status !== 'in_progress') return null;

  const waitSec = Number(job.estimated_wait_time || 0);
  const procSec = Number(job.estimated_processing_time || 0);

  if (!waitSec && !procSec) return null;

  const createdMs = Date.parse(job.created_time || job.createdTime || '');
  const startMs = Date.parse(job.startTime || job.start_time || '');

  let doneAt: number | null = null;

  if (status === 'pending') {
    if (Number.isFinite(createdMs)) {
      doneAt = createdMs + (waitSec + procSec) * 1000;
    }
  } else if (status === 'in_progress') {
    if (Number.isFinite(startMs)) {
      doneAt = startMs + procSec * 1000;
    } else if (Number.isFinite(createdMs)) {
      doneAt = createdMs + (waitSec + procSec) * 1000;
    }
  }

  if (!doneAt) return null;

  const remainingSec = (doneAt - nowMs) / 1000;

  if (remainingSec <= 0) return null;
  if (remainingSec > 7200) return null;

  return remainingSec;
}

function formatRemaining(remainingSec: number): string {
  const s = Math.round(remainingSec);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  return `${h} h`;
}

const statusBadgeColors: Record<string, string> = {
  draft: 'bg-secondary text-muted-foreground',
  pending: 'bg-info-light text-info border-info/25',
  completed: 'bg-primary-muted text-primary-hover border-primary/30',
  failed: 'bg-destructive-light text-destructive border-destructive/25',
  in_progress: 'bg-warning-light text-warning border-warning/25',
};

function StatusBadge({ status }: { status: string }) {
  const label = (status || '').toLowerCase();
  const colors = statusBadgeColors[label] ?? '';
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border border-border/60 ${colors}`}
    >
      {status}
    </span>
  );
}

export default function JobList({ template }: JobListProps) {
  const { startCreateNew, openJob, drafts, loadDrafts, openDraft, removeDraft, refetch, projectId } =
    useShake();

  useEffect(() => {
    loadDrafts(template.id);
  }, [template.id, loadDrafts]);

  const jobs = [...template.jobs].sort((a, b) => {
    const aDate = new Date(a.created_time || '').getTime() || 0;
    const bDate = new Date(b.created_time || '').getTime() || 0;
    return bDate - aDate;
  });

  const videos = template.example_videos || [];
  const [active, setActive] = useState<ExampleVideo | null>(videos[0] ?? null);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);

  const handleRefresh = () => {
    if (projectId) {
      refetch(projectId);
    }
  };

  return (
    <div>
      <button
        type="button"
        className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
        onClick={startCreateNew}
      >
        Create new
      </button>

      {/* Drafts */}
      {drafts.length > 0 && (
        <>
          <h4 className="mt-[18px]">Drafts</h4>
          <ul className="list-none p-0 mt-2.5">
            {drafts.map((d: Draft) => (
              <li
                key={d.savedAt}
                className="mt-2.5 rounded-xl border border-dashed border-border p-3"
              >
                <div className="flex items-center justify-between gap-3 max-md:flex-wrap max-md:gap-2">
                  <span className="text-sm font-semibold text-foreground">{d.name}</span>
                  <div className="flex gap-2.5 max-md:flex-wrap">
                    <button
                      className="bg-transparent border-0 text-primary text-sm underline cursor-pointer p-0 hover:text-primary-hover"
                      onClick={() => openDraft(d)}
                    >
                      Open draft
                    </button>
                    <button
                      className="bg-transparent border-0 text-destructive text-sm underline cursor-pointer p-0 hover:text-destructive-hover"
                      onClick={() => removeDraft(d.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex items-center justify-between gap-3">
        <h4 className="mt-[18px]">Jobs</h4>
        <button
          type="button"
          className="rounded-lg border border-border/60 p-1.5 text-muted-foreground transition-all hover:border-primary hover:bg-secondary hover:text-primary active:scale-95"
          onClick={handleRefresh}
          title="Refresh"
          aria-label="Refresh jobs"
        >
          <RefreshCw size={16} className="block" />
        </button>
      </div>
      {jobs.length === 0 && <div className="mt-1.5 text-sm text-muted-foreground">No jobs yet</div>}

      <ul className="list-none p-0 mt-2.5">
        {jobs.map((j) => {
          const remaining = getRemainingSeconds(j, now);
          const etaLabel = remaining != null ? formatRemaining(remaining) : null;

          return (
            <li
              key={j.id}
              className="mt-2.5 cursor-pointer rounded-xl border border-border/60 bg-card p-3 shadow-sm transition-all hover:border-border hover:shadow-md"
              onClick={() => openJob(j)}
              role="button"
            >
              <div className="flex items-center justify-between gap-3 max-md:flex-wrap max-md:gap-2">
                <span className="text-sm font-semibold text-foreground">{j.name}</span>
                <div className="flex items-center gap-2 max-md:flex-wrap">
                  {etaLabel && (
                    <span className="text-xs text-muted-foreground">~{etaLabel} left</span>
                  )}
                  {j.status && <StatusBadge status={j.status} />}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Sample videos */}
      {videos.length > 0 && active && (
        <>
          <h4 className="mt-[18px]">Sample</h4>

          <VideoPlayer src={active.url} title={active.name} />

          <div
            className="grid grid-cols-[repeat(auto-fill,minmax(90px,90px))] gap-3 mt-3 max-md:grid-cols-[repeat(auto-fill,minmax(70px,70px))] max-md:gap-2"
            role="list"
          >
            {videos.map((v) => {
              const isActive = v.name === active.name;
              return (
                <VideoThumb
                  key={v.name}
                  url={v.url}
                  name={v.name}
                  active={isActive}
                  onClick={() => {
                    if (!isActive) setActive(v);
                  }}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

interface VideoThumbProps {
  url: string;
  name: string;
  active?: boolean;
  onClick?: () => void;
  displayName?: boolean;
}

export function VideoThumb({ url, name, active, onClick, displayName = true }: VideoThumbProps) {
  const thumb = useVideoThumbnail(url);

  return (
    <button
      onClick={onClick}
      className="grid gap-1.5 bg-transparent border-0 text-left cursor-pointer max-w-[90px] min-w-0 overflow-hidden max-md:max-w-[70px]"
    >
      {thumb ? (
        <img
          src={thumb}
          alt={name}
          className={`w-full aspect-square object-cover rounded-xl border-2 transition-all ${
            active ? 'border-primary shadow-md' : 'border-transparent opacity-70 hover:opacity-100'
          }`}
        />
      ) : (
        <div
          className={`w-full aspect-square bg-accent rounded-xl border-2 transition-all ${
            active ? 'border-primary shadow-md' : 'border-transparent opacity-70 hover:opacity-100'
          }`}
        />
      )}

      {displayName && (
        <div className="text-xs text-muted-foreground break-all whitespace-normal max-md:text-[11px]">
          {name}
        </div>
      )}
    </button>
  );
}
